/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect, useState} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import {Button} from '../../../components/Button/Button'
import {Input} from '../../../components/Input/Input'
import {Loading} from '../../../components/Loading/Loading'
import {NewDatatable} from '../../../components/NewDataTable/NewDataTable'
import {DELETE, GET, POST} from '../../../services/api'
import Swal from 'sweetalert2'
import {useNavigate} from 'react-router-dom'
import {useSwitchStatus} from '../../../components/Datatable/SwitchStatus'

type SvgIcon = {
  id: number
  description?: string | null
  svg?: string | null
}

type ReviewHotelCategoryItem = {
  id: number
  title: string
}

type LinkedEntertainmentItem = {
  id: number
  title: string
}

type ReviewHotelFormItem = {
  id: number
  pluginspaceId: number
  status: boolean
  created?: string | null
  updated?: string | null
  title: string
  activeDetails: boolean
  iconId?: number | null
  order: number
  activeRating: boolean
  icon?: SvgIcon | null
  category?: ReviewHotelCategoryItem | null
  // Novas relações
  hotelExperience?: LinkedEntertainmentItem | null
  hotelActivity?: LinkedEntertainmentItem | null
  hotelTour?: LinkedEntertainmentItem | null
}

type ReviewHotelFormFilters = {
  status?: string
  activeRating?: string
  activeDetails?: string
  reviewHotelCategoryId?: string
}

const initialFilters: ReviewHotelFormFilters = {
  status: '',
  activeRating: '',
  activeDetails: '',
  reviewHotelCategoryId: '',
}

const ReviewsHotelIndex: FC = () => {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [forms, setForms] = useState<ReviewHotelFormItem[]>([])
  const [filters, setFilters] = useState<ReviewHotelFormFilters>(initialFilters)

  const [icons, setIcons] = useState<SvgIcon[]>([])
  const [categories, setCategories] = useState<ReviewHotelCategoryItem[]>([])
  const {switchStatus} = useSwitchStatus()

  const getIconDescription = (item: ReviewHotelFormItem) => {
    return (
      item.icon?.description || icons.find((icon) => icon.id === item.iconId)?.description || '-'
    )
  }

  // Função que identifica e formata qual o entretenimento vinculado
  const getEntertainmentLabel = (item: ReviewHotelFormItem) => {
    if (item.hotelExperience?.id) return `${item.hotelExperience.title} (Exp.)`
    if (item.hotelActivity?.id) return `${item.hotelActivity.title} (Ativ.)`
    if (item.hotelTour?.id) return `${item.hotelTour.title} (Tour)`
    return '-'
  }

  const formatData = (data: ReviewHotelFormItem[]) => {
    return data.map((item) => ({
      ...item,
      titleLabel: item.title || '-',
      categoryLabel: item.category?.title || 'Geral',
      entertainmentLabel: getEntertainmentLabel(item), // Usando a nova função
      iconLabel: getIconDescription(item),
      orderLabel: item.order ?? 0,
      statusLabel: item.status ? 1 : 0,
      activeRatingLabel: item.activeRating ? 'Sim' : 'Não',
      detailsLabel: item.activeDetails ? 'Sim' : 'Não',
      createdLabel: item.created ? new Date(item.created).toLocaleString('pt-BR') : '-',
      updatedLabel: item.updated ? new Date(item.updated).toLocaleString('pt-BR') : '-',
    }))
  }

  const fetchDataDependencies = async () => {
    try {
      const [iconsRes, catRes] = await Promise.all([
        POST('/svgwebapp/filter', {}),
        GET('/review-hotel-category'),
      ])

      const iconsList = Array.isArray(iconsRes?.data)
        ? iconsRes.data
        : Array.isArray(iconsRes?.dados)
        ? iconsRes.dados
        : []
      const catList = Array.isArray(catRes?.data)
        ? catRes.data
        : Array.isArray(catRes?.dados)
        ? catRes.dados
        : []

      setIcons(iconsList)
      setCategories(catList)
    } catch (error) {
      console.log(error)
      setIcons([])
      setCategories([])
    }
  }

  const getReviewHotelForms = async (searchFilters?: ReviewHotelFormFilters) => {
    try {
      const filtersToUse = searchFilters || filters
      const payload: Record<string, any> = {}

      if (filtersToUse.status === 'true') payload.status = true
      if (filtersToUse.status === 'false') payload.status = false
      if (filtersToUse.activeRating === 'true') payload.activeRating = true
      if (filtersToUse.activeRating === 'false') payload.activeRating = false
      if (filtersToUse.activeDetails === 'true') payload.activeDetails = true
      if (filtersToUse.activeDetails === 'false') payload.activeDetails = false
      if (filtersToUse.reviewHotelCategoryId)
        payload.reviewHotelCategoryId = filtersToUse.reviewHotelCategoryId

      const queryParams = new URLSearchParams(
        Object.entries(payload).reduce<Record<string, string>>((acc, [key, value]) => {
          acc[key] = String(value)
          return acc
        }, {})
      )

      const res = await GET(`/review-hotel-form${queryParams.toString() ? `?${queryParams}` : ''}`)
      const list = Array.isArray(res?.data) ? res.data : Array.isArray(res?.dados) ? res.dados : []

      setForms(list)
    } catch (error) {
      console.log(error)
      setForms([])
      Swal.fire({
        icon: 'error',
        title: 'Erro ao carregar',
        text: 'Não foi possível carregar os formulários de review.',
      })
    }
  }

  const handleFilterChange = (field: keyof ReviewHotelFormFilters, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleSearch = async () => {
    setLoading(true)
    try {
      await getReviewHotelForms(filters)
    } finally {
      setLoading(false)
    }
  }

  const handleClearFilters = async () => {
    setLoading(true)
    try {
      setFilters(initialFilters)
      await getReviewHotelForms(initialFilters)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (id: number) => {
    const confirmResult = await Swal.fire({
      icon: 'warning',
      title: 'Excluir formulário?',
      text: 'Esta ação não pode ser desfeita.',
      showCancelButton: true,
      confirmButtonText: 'Excluir',
      cancelButtonText: 'Cancelar',
    })

    if (!confirmResult.isConfirmed) return

    try {
      setLoading(true)
      const res = await DELETE(`/review-hotel-form/${id}`)

      if (res?.success) {
        Swal.fire({
          icon: 'success',
          title: 'Excluído com sucesso',
          text: 'O formulário foi removido.',
        })
        await getReviewHotelForms()
      } else {
        throw new Error(res?.message || 'Erro ao excluir o formulário')
      }
    } catch (error: any) {
      console.log(error)
      Swal.fire({
        icon: 'error',
        title: 'Erro ao excluir',
        text: error?.message || 'Não foi possível excluir o formulário.',
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const initPage = async () => {
      setLoading(true)
      try {
        await fetchDataDependencies()
        await getReviewHotelForms()
      } catch (error) {
        console.error('Erro ao inicializar página:', error)
      } finally {
        setLoading(false)
      }
    }

    initPage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>Formulários</span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  Cadastre os formulários de opinião sobre o hotel.
                </span>
              </h3>
              <div className='card-toolbar'>
                <Button
                  text='Criar formulário'
                  color='primary'
                  iconColor='#FFF'
                  icon='fas fa-plus'
                  size='small'
                  horizontalPadding={6}
                  btnAttr={{type: 'button'}}
                  click={() => navigate('/hospitality/feed-back/content/criar')}
                  btnClass='mb-5 fw-bolder'
                />
              </div>
            </div>

            <div className='card-body border-bottom'>
              <div className='row g-3'>
                <div className='col-md-3'>
                  <Input
                    label='Categoria'
                    inputAttr={{type: 'select'}}
                    value={filters.reviewHotelCategoryId || ''}
                    change={(value: string) => handleFilterChange('reviewHotelCategoryId', value)}
                    options={[
                      {value: '', select: 'Todas'},
                      ...categories.map((c) => ({value: String(c.id), select: c.title})),
                    ]}
                  />
                </div>

                <div className='col-md-2'>
                  <Input
                    label='Status'
                    inputAttr={{type: 'select'}}
                    value={filters.status || ''}
                    change={(value: string) => handleFilterChange('status', value)}
                    options={[
                      {value: '', select: 'Todos'},
                      {value: 'true', select: 'Ativo'},
                      {value: 'false', select: 'Inativo'},
                    ]}
                  />
                </div>

                <div className='col-md-2'>
                  <Input
                    label='Avaliação'
                    inputAttr={{type: 'select'}}
                    value={filters.activeRating || ''}
                    change={(value: string) => handleFilterChange('activeRating', value)}
                    options={[
                      {value: '', select: 'Todos'},
                      {value: 'true', select: 'Sim'},
                      {value: 'false', select: 'Não'},
                    ]}
                  />
                </div>

                <div className='col-md-2'>
                  <Input
                    label='Detalhes'
                    inputAttr={{type: 'select'}}
                    value={filters.activeDetails || ''}
                    change={(value: string) => handleFilterChange('activeDetails', value)}
                    options={[
                      {value: '', select: 'Todos'},
                      {value: 'true', select: 'Sim'},
                      {value: 'false', select: 'Não'},
                    ]}
                  />
                </div>

                <div className='col-12 d-flex justify-content-end gap-2'>
                  <Button text='Limpar' color='danger' size='small' click={handleClearFilters} />
                  <Button text='Filtrar' color='primary' size='small' click={handleSearch} />
                </div>
              </div>
            </div>

            <div className='card-body'>
              <NewDatatable
                data={formatData(forms)}
                headers={[
                  {key: 'Título', value: 'titleLabel', type: 'default'},
                  {key: 'Entretenimento', value: 'entertainmentLabel', type: 'default'},
                  {key: 'Categoria', value: 'categoryLabel', type: 'default'},
                  {key: 'Ordem', value: 'orderLabel', type: 'default'},
                  {
                    key: 'Status',
                    value: 'statusLabel',
                    type: 'check',
                    action: (item: any) =>
                      switchStatus({
                        endpoint: 'review-hotel-form',
                        Id: item.id,
                        status: item.status,
                        statusKey: 'status',
                      }),
                  },
                  {key: 'Ações', value: 'acoes', type: ''},
                ]}
                options={[
                  {
                    icon: 'fas fa-pen',
                    action: (item) => navigate(`/hospitality/feed-back/content/edit/${item.id}`),
                    title: 'Editar formulário',
                  },
                  {
                    icon: 'fas fa-trash',
                    action: (item) => handleDelete(item.id),
                    title: 'Excluir formulário',
                  },
                ]}
                color='primary'
                search
              />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const ReviewsHotel: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Dashboard', path: 'dashboard', isActive: false},
          {title: 'Hospitality', path: 'hospitality', isActive: false},
          {
            title: 'Formulários',
            path: 'hospitality/feed-back/content',
            isActive: true,
          },
        ]}
      >
        Formulários
      </PageTitle>
      <ReviewsHotelIndex />
    </>
  )
}

export {ReviewsHotel}
